import React, { useState } from 'react';
import { Card, Form, Button, Alert, Table, Badge, Spinner } from 'react-bootstrap';
import { FaSearch, FaHistory, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { verifyAdvancedPayment } from '../../api/services/AdminService';

const PaymentRecovery = () => {
    const [searchType, setSearchType] = useState('email');
    const [searchValue, setSearchValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!searchValue.trim()) return;

        setLoading(true);
        setResult(null);
        setError(null);

        try {
            const data = searchType === 'email'
                ? { email: searchValue.trim() }
                : { applicationId: searchValue.trim() };

            const res = await verifyAdvancedPayment(data);
            setResult(res);
        } catch (err) {
            setError(err.response?.data?.message || 'Verification failed. Please check the ID/Email and try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card className="shadow-sm border-0 mb-4">
            <Card.Header className="bg-white border-0 py-3">
                <h5 className="mb-0 d-flex align-items-center">
                    <FaHistory className="me-2 text-primary" /> Advanced Payment Recovery
                </h5>
                <small className="text-muted">Search and verify missing payments directly from Cashfree records</small>
            </Card.Header>
            <Card.Body>
                <Form onSubmit={handleSearch}>
                    <div className="d-flex gap-3 mb-4">
                        <Form.Check
                            type="radio"
                            label="Search by Email"
                            name="searchType"
                            checked={searchType === 'email'}
                            onChange={() => setSearchType('email')}
                        />
                        <Form.Check
                            type="radio"
                            label="Search by Application ID"
                            name="searchType"
                            checked={searchType === 'appId'}
                            onChange={() => setSearchType('appId')}
                        />
                    </div>

                    <div className="d-flex gap-2">
                        <Form.Control
                            type="text"
                            placeholder={searchType === 'email' ? "Enter student email..." : "Enter MongoDB Application ID..."}
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            className="bg-light border-0"
                        />
                        <Button variant="primary" type="submit" disabled={loading} className="px-4">
                            {loading ? <Spinner size="sm" animation="border" /> : <><FaSearch className="me-2" /> Search</>}
                        </Button>
                    </div>
                </Form>

                {error && <Alert variant="danger" className="mt-4 border-0">{error}</Alert>}

                {result && (
                    <div className="mt-4 animate__animated animate__fadeIn">
                        <div className="p-3 bg-light rounded mb-4 d-flex justify-content-between align-items-center">
                            <div>
                                <h6 className="mb-1 fw-bold">{result.application.fullName}</h6>
                                <p className="mb-0 text-muted small">Application: {result.application.applicationNo}</p>
                            </div>
                            <div className="text-end">
                                <Badge bg={result.application.paymentStatus === 'PAID' ? 'success' : 'warning'}>
                                    {result.application.paymentStatus}
                                </Badge>
                                <div className="small text-muted mt-1">{result.message}</div>
                            </div>
                        </div>

                        <h6 className="mb-3 fw-bold small">Payment Attempts found in Database:</h6>
                        <Table responsive hover className="small align-middle border">
                            <thead className="bg-white">
                                <tr>
                                    <th>Order ID</th>
                                    <th>Gateway Status</th>
                                    <th>Database Status</th>
                                    <th>Amount</th>
                                    <th>Result</th>
                                </tr>
                            </thead>
                            <tbody>
                                {result.verificationResults.map((res, idx) => (
                                    <tr key={idx}>
                                        <td>{res.orderId}</td>
                                        <td>
                                            <Badge bg={res.status === 'PAID' ? 'success' : 'secondary'}>{res.status}</Badge>
                                        </td>
                                        <td>
                                            <Badge bg={res.localStatus === 'SUCCESS' ? 'success' : 'warning'}>{res.localStatus}</Badge>
                                        </td>
                                        <td>₹{res.amount || 'N/A'}</td>
                                        <td>
                                            {res.status === 'PAID' ?
                                                <span className="text-success"><FaCheckCircle className="me-1" /> Recovered</span> :
                                                <span className="text-muted">No Action</span>
                                            }
                                        </td>
                                    </tr>
                                ))}
                                {result.verificationResults.length === 0 && (
                                    <tr>
                                        <td colSpan="5" className="text-center py-3 text-muted">No payment attempts found in database for this application.</td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>

                        {result.application.paymentStatus !== 'PAID' && (
                            <div className="p-3 bg-warning bg-opacity-10 border border-warning rounded d-flex align-items-start">
                                <FaExclamationTriangle className="text-warning me-3 mt-1" />
                                <div>
                                    <h6 className="mb-1 fw-bold text-warning-emphasis">Payment Not Found</h6>
                                    <p className="mb-0 small text-warning-emphasis">
                                        If the student insists they have paid, ask for their transaction ID and verify it manually in the Cashfree dashboard.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

export default PaymentRecovery;
